import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import "react-lazy-load-image-component/src/effects/blur.css";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import axios from "../../../axios-backend";
import BackButton from "../../main/BackButton";
import Line from "../../main/Line";
import SwiperSlider from "../../main/SwiperSlider";
import Text from "../../main/Text";
import classes from "../css/Home.module.css";
import "./css/ProductDetails.css";
import { LazyImageWithLoader } from "../LazyImageWithLoader";

const ProductDetails = (props) => {
  const navigate = useNavigate();
  const location = useLocation();

  // All hooks must be called before any conditional returns
  const [images, setImages] = useState([]);
  const [typeDetails, setTypeDetails] = useState();
  const [loading, setLoading] = useState(false);

  // Get state values safely
  const state = location.state || {};
  const {
    productId,
    type,
    areaAmount,
    gender,
    size,
    singleProduct = false,
  } = state;

  useEffect(() => {
    // Redirect if no state
    if (!location.state) {
      navigate('/');
      return;
    }

    // Only fetch images if we have productId and gender
    if (productId && (gender === 0 || gender === 1)) {
      axios
        .get(`/get-imgs-by-pid`, {
          params: {
            id: productId,
            type: 1,
            gender,
            usrType: "usr",
          },
        })
        .then((data) => {
          setImages(data.data.imgs);
        });
    }
  }, [productId, gender, location.state, navigate]);

  useEffect(() => {
    // Only fetch type details if we have type
    if (type && type !== "") {
      setLoading(true);
      axios
        .get(`/type?id=${type}`)
        .then((data) => {
          if (data?.data?.type) setTypeDetails(data?.data?.type);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
        });
    }
  }, [type]);

  useEffect(() => {
    if (location.state) {
      const scrollPosition = Cookies.get("scrollPositionPD");
      if (scrollPosition) {
        window.scrollTo(0, parseInt(scrollPosition));
      }
    }
  }, [location.state]);



  const handleScroll = () => {
    const currentScrollPosition = window.pageYOffset;
    Cookies.set("scrollPositionPD", currentScrollPosition);
  };
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  const productKey = `scrollPositionPD-${productId}`;
  const [imagesLoaded, setImagesLoaded] = useState(0);

  // Restore scroll position only after all images are loaded
  useEffect(() => {
    if (images.length > 0 && imagesLoaded === images.length) {
      const pos = sessionStorage.getItem(productKey);
      if (pos) {
        window.scrollTo(0, parseInt(pos));
      }
    }
  }, [imagesLoaded, images.length, productKey]);

  // Save scroll position for this product on NavLink/button click
  // Anchor-based scroll restoration
  const saveAnchor = (anchorId) => {
    sessionStorage.setItem('productDetailsAnchor', anchorId);
    sessionStorage.setItem(productKey, window.pageYOffset); // fallback
  };

  // Disable browser scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Restore scroll position on mount, after a short delay
  useEffect(() => {
    const anchorId = sessionStorage.getItem('productDetailsAnchor');
    let retries = 0;
    let maxRetries = 10;
    let interval = null;
    function tryScrollToAnchor() {
      const anchorElem = anchorId && document.getElementById(anchorId);
      if (anchorElem) {
        anchorElem.scrollIntoView({ behavior: 'auto', block: 'center' });
        sessionStorage.removeItem('productDetailsAnchor');
        clearInterval(interval);
      } else if (++retries >= maxRetries) {
        clearInterval(interval);
      }
    }
    if (anchorId) {
      interval = setInterval(tryScrollToAnchor, 100);
      // Also try once after a short delay in case it's ready immediately
      setTimeout(tryScrollToAnchor, 100);
    } else {
      const pos = sessionStorage.getItem(productKey);
      if (pos) {
        setTimeout(() => {
          window.scrollTo(0, parseInt(pos));
        }, 50);
      }
    }
    return () => interval && clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Also trigger anchor scroll after all images are loaded
  useEffect(() => {
    const anchorId = sessionStorage.getItem('productDetailsAnchor');
    if (images.length > 0 && imagesLoaded === images.length && anchorId) {
      const anchorElem = document.getElementById(anchorId);
      if (anchorElem) {
        anchorElem.scrollIntoView({ behavior: 'auto', block: 'center' });
        sessionStorage.removeItem('productDetailsAnchor');
      }
    }
  }, [imagesLoaded, images.length]);

  // Early return if no state
  if (!location.state) {
    return null;
  }

  // if it is preSale dont render anything else
  if (typeDetails?.preSale) {
    return (
      <>
        <BackButton />
        <Text>{typeDetails?.preSaleText}</Text>
      </>
    );
  }

  return !loading ? (
    <>
      <BackButton />
      {!typeDetails?.issoldout ? (
        <>
          <div className="singleContainer">
            <SwiperSlider>
              {images?.map((i, index) => {
                const src = i.url.startsWith("s3")
                  ? `https://wonwonleywon-images.s3.amazonaws.com/product_images/${i.url}`
                  : `${process.env.REACT_APP_Url}${i.url}`;
                return (
                  <LazyImageWithLoader
                    src={src}
                    alt={`Product ${index}`}
                    style={{
                      minHeight: "300px",
                      maxHeight: "500px",
                      minWidth: "300px",
                      maxWidth: "500px",
                      padding: "10px 0",
                    }}
                    onLoad={() => setImagesLoaded((prev) => prev + 1)}
                  />
                );
              })}
            </SwiperSlider>
          </div>
          <Line type="top-line">
            <NavLink
              id="purchase-link"
              to="/products/purchase"
              state={{ product: productId, type }}
              className={classes.navLink}
              onClick={() => saveAnchor('purchase-link')}
            >
              Purchase
            </NavLink>
          </Line>

          {!singleProduct && (
            <Line type="top-line">
              <NavLink
                id="change-color-link"
                to="/products/shopproduct"
                state={{ type, gender, areaAmount, size }}
                className={classes.navLink}
              >
                Change Color
              </NavLink>
            </Line>
          )}
          {size && size !== "null" ? (
            <Line type="top-line">
              <NavLink
                id="change-size-link"
                to="/products/sizepicker"
                state={{ type, gender, areaAmount }}
                className={classes.navLink}
                onClick={() => saveAnchor('change-size-link')}
              >
                Change Size
              </NavLink>
            </Line>
          ) : null}

          <Line type="top-line">
            <NavLink
              id="philanthropy-link"
              to="/products/material"
              state={{ tid: type, title: "philanthropy" }}
              className={classes.navLink}
              onClick={() => saveAnchor('philanthropy-link')}
            >
              philanthropy
            </NavLink>
          </Line>
          <Line type="top-line">
            <NavLink
              id="price-link"
              to="/products/material"
              state={{ tid: type, title: "proPrice" }}
              className={classes.navLink}
              onClick={() => saveAnchor('price-link')}
            >
              price
            </NavLink>
          </Line>
          <Line type="top-line">
            <NavLink
              id="name-link"
              to="/products/material"
              state={{ tid: type, title: "nameOf" }}
              className={classes.navLink}
              onClick={() => saveAnchor('name-link')}
            >
              Name of Product
            </NavLink>
          </Line>
          {!singleProduct && (
            <Line type="top-line">
              <NavLink
                id="collection-video-link"
                to="/products/productvideo"
                state={{ type, gender }}
                className={classes.navLink}
                onClick={() => saveAnchor('collection-video-link')}
              >
                Collection Video
              </NavLink>
            </Line>
          )}

          {size && size !== "null" ? (
            <Line type="top-line">
              <NavLink
                id="find-size-link"
                to="/products/productsizeguide"
                state={{ type, gender }}
                className={classes.navLink}
                onClick={() => saveAnchor('find-size-link')}
              >
                Find your size
              </NavLink>
            </Line>
          ) : (
            <Line type="top-line">
              <NavLink
                id="dimensions-link"
                to="/products/material"
                state={{ tid: type, title: "dimensionDetails" }}
                className={classes.navLink}
                onClick={() => saveAnchor('dimensions-link')}
              >
                product dimensions
              </NavLink>
            </Line>
          )}

          <Line type="top-line">
            <NavLink
              id="material-link"
              to="/products/material"
              state={{ tid: type, title: "material" }}
              className={classes.navLink}
              onClick={() => saveAnchor('material-link')}
            >
              Material
            </NavLink>
          </Line>
          <Line type="top-line">
            <NavLink
              id="madein-link"
              to="/products/material"
              state={{ tid: type, title: "madeIn" }}
              className={classes.navLink}
              onClick={() => saveAnchor('madein-link')}
            >
              Made In
            </NavLink>
          </Line>
          <Line type="top-line">
            <NavLink
              id="product-images-link"
              to="/products/material"
              state={{ tid: type, title: "proImage" }}
              className={classes.navLink}
              onClick={() => saveAnchor('product-images-link')}
            >
              Product Images
            </NavLink>
          </Line>
          <Line type="top-line">
            <NavLink
              id="care-link"
              to="/products/material"
              state={{ tid: type, title: "care" }}
              className={classes.navLink}
              onClick={() => saveAnchor('care-link')}
            >
              care
            </NavLink>
          </Line>
          <Line type="top-line">
            <NavLink
              id="duties-link"
              to="/products/material"
              state={{ tid: type, title: "duties" }}
              className={classes.navLink}
              onClick={() => saveAnchor('duties-link')}
            >
              Duties
            </NavLink>
          </Line>
          <Line type="top-line">
            <NavLink
              id="taxes-link"
              to="/products/material"
              state={{ tid: type, title: "taxes" }}
              className={classes.navLink}
              onClick={() => saveAnchor('taxes-link')}
            >
              taxes
            </NavLink>
          </Line>
          <Line type="top-line">
            <NavLink
              id="shipping-methods-link"
              to="/products/material"
              state={{ tid: type, title: "shippingMethod" }}
              className={classes.navLink}
              onClick={() => saveAnchor('shipping-methods-link')}
            >
              Shipping Methods
            </NavLink>
          </Line>
          <Line type="top-line">
            <NavLink
              id="shipping-costs-link"
              to="/products/material"
              state={{ tid: type, title: "shippingCost" }}
              className={classes.navLink}
              onClick={() => saveAnchor('shipping-costs-link')}
            >
              Shipping Costs
            </NavLink>
          </Line>
          <Line type="top-line">
            <NavLink
              id="delivery-time-link"
              to="/products/material"
              state={{ tid: type, title: "deliveryTime" }}
              className={classes.navLink}
              onClick={() => saveAnchor('delivery-time-link')}
            >
              Delivery Time
            </NavLink>
          </Line>
          {/* <Line type="top-line">
        <NavLink to="/products/material" state={{ pid:productId, title: 'tracking' }} className={classes.navLink}>
          Tracking
          </NavLink>
      </Line> */}
          <Line type="top-line">
            <NavLink
              id="returns-link"
              to="/products/material"
              state={{ tid: type, title: "returns" }}
              className={classes.navLink}
              onClick={() => saveAnchor('returns-link')}
            >
              Returns
            </NavLink>
          </Line>
          {/* <Line type="top-line">
        <NavLink to="/products/material" state={{ pid:productId, title: 'refund' }} className={classes.navLink}>
          Refund
          </NavLink>
      </Line> */}

          <Line type="top-line">
            <NavLink
              id="prices-currencies-link"
              to="/products/material"
              state={{ tid: type, title: "priceAndCurrency" }}
              className={classes.navLink}
              onClick={() => saveAnchor('prices-currencies-link')}
            >
              Prices and Currencies
            </NavLink>
          </Line>
          <Line type="top-line">
            <NavLink
              id="change-country-link"
              to="/products/material"
              state={{ tid: type, title: "changeCountry" }}
              className={classes.navLink}
              onClick={() => saveAnchor('change-country-link')}
            >
              Change Country and Currency
            </NavLink>
          </Line>
          <Line type="top-line">
            <NavLink
              id="payment-methods-link"
              to="/products/material"
              state={{ tid: type, title: "paymentMethod" }}
              className={classes.navLink}
              onClick={() => saveAnchor('payment-methods-link')}
            >
              Payment Methods
            </NavLink>
          </Line>
          <Line type="top-line">
            <NavLink
              id="international-fees-link"
              to="/products/material"
              state={{ tid: type, title: "international" }}
              className={classes.navLink}
              onClick={() => saveAnchor('international-fees-link')}
            >
              International Transaction Fees
            </NavLink>
          </Line>
          <Line type="top-line">
            <NavLink
              id="contact-link"
              to="/more/contact_us"
              className={classes.navLink}
              onClick={() => saveAnchor('contact-link')}
            >
              Contact Us
            </NavLink>
          </Line>
        </>
      ) : (
        <Line style={{ fontWeight: "500" }}>this product is sold.</Line>
      )}
    </>
  ) : (
    <></>
  );
};
export default ProductDetails;